import { Tag } from 'antd';
import { FilterFieldConfig } from 'common/dynamic-filter-form';
import { formatDateTime } from 'utils/formatDate';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  voucher: 'Voucher',
};

const TRANSACTION_STATUS_LABELS: Record<number, { label: string; color: string }> = {
  1001: { label: 'Authorize', color: 'blue' },
  1002: { label: 'Capture', color: 'green' },
  1003: { label: 'Canceled', color: 'red' },
};

// Same values/colors as iap-notifications/config.tsx's ENVIRONMENT_COLORS.
const ENVIRONMENT_COLORS: Record<string, string> = {
  Sandbox: 'orange',
  Production: 'green',
};

// Read-only for this first cut - deleting an individual transaction stays
// on the plan detail page's preview (subscriptions/detail.tsx), which
// already has the confirmation dialog + Sandbox-vs-Production safeguard.
// This page's job is browsing/searching across every plan, not moderation.
export const columnsGenerator = () => [
  {
    accessorKey: 'user.email',
    header: 'Email',
    cell: ({ row }) => row.original?.user?.email ?? '-',
  },
  {
    accessorKey: 'subscription_user.subscription_id',
    header: 'Plan',
    cell: ({ row }) => row.original?.subscription_user?.subscription?.title ?? '-',
  },
  {
    accessorKey: 'method',
    header: 'Method',
    cell: ({ getValue }) => PAYMENT_METHOD_LABELS[getValue()] ?? getValue(),
  },
  { accessorKey: 'amount', header: 'Amount' },
  {
    accessorKey: 'amount_charged',
    header: 'Amount Charged',
    cell: ({ getValue }) => {
      const value = getValue();
      return value == null ? '-' : value;
    },
  },
  {
    accessorKey: 'is_trial',
    header: 'Trial',
    cell: ({ getValue }) => (getValue() ? <Tag color="blue">Trial</Tag> : '-'),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const meta = TRANSACTION_STATUS_LABELS[getValue()];
      return meta ? <Tag color={meta.color}>{meta.label}</Tag> : getValue();
    },
  },
  {
    accessorKey: 'environment',
    header: 'Environment',
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return '-';
      return <Tag color={ENVIRONMENT_COLORS[value] ?? 'default'}>{value}</Tag>;
    },
  },
  { accessorKey: 'tx_id', header: 'TxID' },
  { accessorKey: 'tx_product_id', header: 'Product ID' },
  {
    accessorKey: 'subscription_user.apple_original_transaction_id',
    header: 'Apple Original Tx ID',
    cell: ({ row }) =>
      row.original?.subscription_user?.apple_original_transaction_id || '-',
  },
  {
    accessorKey: 'subscription_user.google_purchase_token',
    header: 'Google Purchase Token',
    cell: ({ row }) =>
      row.original?.subscription_user?.google_purchase_token || '-',
  },
  {
    accessorKey: 'created_at',
    header: 'Created At',
    cell: ({ getValue }) => formatDateTime(getValue()),
  },
];

// subscription_id options are fetched at render time (see pages.tsx) rather
// than hardcoded - unlike this codebase's usual small-fixed-set pattern
// (e.g. companyOptions), subscription plans are a handful today but do get
// added over time.
export const filterConfig = (
  subscriptionOptions: { value: string; label: string }[]
): FilterFieldConfig[] => [
  {
    name: 'subscription_id',
    type: 'select',
    label: 'Plan',
    placeholder: 'All',
    minWidth: '160px',
    options: subscriptionOptions,
    defaultValue: 'all',
  },
  {
    name: 'environment',
    type: 'select',
    label: 'Environment',
    placeholder: 'All',
    minWidth: '120px',
    options: [
      { value: 'Sandbox', label: 'Sandbox' },
      { value: 'Production', label: 'Production' },
    ],
    defaultValue: 'all',
  },
];
